import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Template-driven custom-field renderer used by task create + edit forms.
 *
 * Props:
 *   taskType   — one of "task" | "story" | "bug" | "epic"
 *   value      — current { [key]: value } map
 *   onChange   — (nextValues) => void
 *   users      — optional array of {id,name} for `user` picker
 *
 * Loads the current template from /api/settings/task-forms/{type}, caches
 * per-mount, and renders one control per field.
 */
export default function DynamicFields({ taskType, value, onChange, users = [], projectId = null }) {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    let alive = true;
    setTemplate(null);
    const params = projectId ? { project_id: projectId } : {};
    api.get(`/settings/task-forms/effective/${taskType}`, { params })
      .then(r => { if (alive) setTemplate({ fields: r.data.fields || [] }); })
      .catch(() => { if (alive) setTemplate({ fields: [] }); });
    return () => { alive = false; };
  }, [taskType, projectId]);

  const set = (k, v) => onChange({ ...(value || {}), [k]: v });

  if (!template) return null;
  if (!template.fields || template.fields.length === 0) return null;

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="eyebrow text-[10px]">{taskType.toUpperCase()} fields</div>
      {template.fields.map(f => {
        const v = (value || {})[f.key];
        const testId = `custom-field-${f.key}`;
        if (f.type === "textarea") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <textarea rows={3} value={v ?? ""} onChange={e => set(f.key, e.target.value)} required={f.required} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid={testId} />
              {f.help && <div className="text-[10px] text-muted-foreground mt-0.5">{f.help}</div>}
            </div>
          );
        }
        if (f.type === "number") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <input type="number" value={v ?? ""} onChange={e => set(f.key, e.target.value === "" ? null : Number(e.target.value))} required={f.required} className="w-full border border-border px-3 py-2 text-sm" data-testid={testId} />
              {f.help && <div className="text-[10px] text-muted-foreground mt-0.5">{f.help}</div>}
            </div>
          );
        }
        if (f.type === "date") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <input type="date" value={v ?? ""} onChange={e => set(f.key, e.target.value || null)} required={f.required} className="w-full border border-border px-3 py-2 text-sm" data-testid={testId} />
            </div>
          );
        }
        if (f.type === "select") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <select value={v ?? ""} onChange={e => set(f.key, e.target.value || null)} required={f.required} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid={testId}>
                <option value="">— Select —</option>
                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {f.help && <div className="text-[10px] text-muted-foreground mt-0.5">{f.help}</div>}
            </div>
          );
        }
        if (f.type === "multiselect") {
          const current = Array.isArray(v) ? v : [];
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <div className="flex flex-wrap gap-1.5">
                {(f.options || []).map(o => {
                  const on = current.includes(o);
                  return (
                    <button
                      type="button"
                      key={o}
                      onClick={() => set(f.key, on ? current.filter(x => x !== o) : [...current, o])}
                      className={`text-xs px-2 py-1 border ${on ? "bg-[#002FA7] border-[#002FA7] text-white" : "bg-white border-border hover:bg-muted"}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        if (f.type === "checkbox") {
          return (
            <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!v} onChange={e => set(f.key, e.target.checked)} data-testid={testId} />
              {f.label}
            </label>
          );
        }
        if (f.type === "url") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <input type="url" placeholder="https://…" value={v ?? ""} onChange={e => set(f.key, e.target.value)} required={f.required} className="w-full border border-border px-3 py-2 text-sm" data-testid={testId} />
            </div>
          );
        }
        if (f.type === "user") {
          return (
            <div key={f.key}>
              <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
              <select value={v ?? ""} onChange={e => set(f.key, e.target.value || null)} required={f.required} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid={testId}>
                <option value="">— None —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          );
        }
        // default: text
        return (
          <div key={f.key}>
            <label className="eyebrow block mb-1 text-[10px]">{f.label}{f.required && " *"}</label>
            <input value={v ?? ""} onChange={e => set(f.key, e.target.value)} required={f.required} className="w-full border border-border px-3 py-2 text-sm" data-testid={testId} />
            {f.help && <div className="text-[10px] text-muted-foreground mt-0.5">{f.help}</div>}
          </div>
        );
      })}
    </div>
  );
}
